import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Mail, Edit3, Loader2, ArrowLeft, CheckCircle2, ExternalLink } from "lucide-react";
import { api, resolveCoverUrl, formatApiError } from "@/lib/api";
import { AGE_GROUPS } from "@/lib/constants";
import { toast } from "sonner";

const MAX_WORDS = 150;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const wordCount = (t) => t.trim().split(/\s+/).filter(Boolean).length;

function statusBadge(status) {
  const map = {
    pending: { label: "Pending review", color: "#D4834A" },
    approved: { label: "Live on shelf", color: "#7FA98F" },
    rejected: { label: "Rejected", color: "#B23B28" },
  };
  const s = map[status] || map.pending;
  return (
    <span
      className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded text-white"
      style={{ background: s.color }}
    >
      {s.label}
    </span>
  );
}

function PageHeader() {
  return (
    <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
      <a
        href="https://www.thebookshelfproject.org"
        className="flex items-center gap-3"
        aria-label="The Bookshelf Project home"
        data-testid="my-books-logo"
      >
        <img
          src="/tbp-logo.png"
          alt="The Bookshelf Project"
          className="h-14 w-auto"
          loading="eager"
        />
      </a>
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm font-semibold text-[#2B328C] hover:text-[#0E232E]"
      >
        <ArrowLeft size={14} /> Back to The Bookshelf
      </Link>
    </div>
  );
}

function RequestLinkPanel() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/request-magic-link", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send link.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div
        className="bg-white border border-[#CFD6DC] rounded-2xl p-8 text-center shadow-sm"
        data-testid="link-sent"
      >
        <CheckCircle2 size={42} className="mx-auto text-[#7FA98F]" />
        <h2 className="mt-3 font-display text-2xl font-extrabold text-[#0E232E]">
          Check your email
        </h2>
        <p className="mt-2 text-sm text-[#2F2E2E] max-w-md mx-auto">
          If you've previously submitted a book, a personal manage-link is on its way to <strong>{email}</strong>. Check your inbox (and spam folder, just in case).
        </p>
        <button
          onClick={() => {
            setSent(false);
            setEmail("");
          }}
          className="mt-5 text-sm text-[#2B328C] underline font-semibold"
        >
          Use a different email
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#CFD6DC] rounded-2xl p-8 shadow-sm">
      <Mail size={28} className="text-[#38B6D2]" />
      <h2 className="mt-3 font-display text-2xl md:text-3xl font-extrabold text-[#0E232E] leading-tight">
        Manage your books
      </h2>
      <p className="mt-2 text-sm text-[#2F2E2E] leading-relaxed">
        Enter the email you used when submitting your book. We'll email you a personal link to edit your details — no password needed.
      </p>
      <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3" data-testid="request-link-form">
        <input
          required
          type="email"
          placeholder="you@example.com"
          className="field-input flex-1"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="request-link-email"
        />
        <button
          type="submit"
          disabled={loading}
          className="btn-teal px-6 py-2.5 rounded-md flex items-center justify-center gap-2"
          data-testid="request-link-submit"
        >
          {loading ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />}
          {loading ? "Sending…" : "Send my link"}
        </button>
      </form>
      <p className="mt-4 text-xs text-[#0E232E]/60">
        For your security, we'll send the link only if there's a submission tied to that email.
      </p>
    </div>
  );
}

function BookEditor({ book, token, onSaved, onCancel }) {
  const [form, setForm] = useState({
    title: book.title,
    age_group: book.age_group,
    description: book.description,
    purchase_url: book.purchase_url,
    author_link: book.author_link || "",
  });
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const wc = wordCount(form.description);
  const overWord = wc > MAX_WORDS;

  const handleFile = (f) => {
    setError("");
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/jpg"].includes(f.type)) {
      setError("Cover must be JPG or PNG.");
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      setError("Cover image exceeds 5MB.");
      return;
    }
    setFile(f);
    setFilePreview(URL.createObjectURL(f));
  };

  const save = async (e) => {
    e.preventDefault();
    if (overWord) return setError("Description exceeds 150 words.");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("token", token);
      fd.append("title", form.title);
      fd.append("age_group", form.age_group);
      fd.append("description", form.description);
      fd.append("purchase_url", form.purchase_url);
      fd.append("author_link", form.author_link || "");
      if (file) fd.append("cover_image", file);
      await api.put(`/author/books/${book.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Saved! Your changes are now under review.");
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      className="bg-white border border-[#CFD6DC] rounded-xl p-6 mt-3 space-y-4"
      data-testid={`book-editor-${book.id}`}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-20 h-28 flex-shrink-0 rounded-md overflow-hidden border border-[#CFD6DC] bg-[#F0F3F5]"
          style={{
            backgroundImage:
              filePreview
                ? `url(${filePreview})`
                : book.cover_image_url
                  ? `url(${resolveCoverUrl(book.cover_image_url)})`
                  : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="flex-1">
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">
            Replace cover (optional)
          </label>
          <input
            type="file"
            accept="image/jpeg,image/png"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="text-xs"
            data-testid={`edit-cover-${book.id}`}
          />
          <p className="text-[11px] text-[#0E232E]/50 mt-1">JPG/PNG, max 5MB</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">
            Title
          </label>
          <input
            required
            className="field-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid={`edit-title-${book.id}`}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">
            Age group
          </label>
          <select
            required
            className="field-input"
            value={form.age_group}
            onChange={(e) => setForm({ ...form, age_group: e.target.value })}
            data-testid={`edit-age-group-${book.id}`}
          >
            {AGE_GROUPS.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label} ({g.range})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-[#0E232E]">
            Description
          </label>
          <span
            className={`text-xs tabular-nums ${overWord ? "text-[#B23B28]" : "text-[#0E232E]/60"}`}
          >
            {wc} / {MAX_WORDS} words
          </span>
        </div>
        <textarea
          required
          rows={4}
          className="field-input resize-y"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          data-testid={`edit-description-${book.id}`}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">
            Purchase URL
          </label>
          <input
            required
            type="url"
            className="field-input"
            value={form.purchase_url}
            onChange={(e) => setForm({ ...form, purchase_url: e.target.value })}
            data-testid={`edit-purchase-url-${book.id}`}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">
            Your website (optional)
          </label>
          <input
            type="url"
            className="field-input"
            value={form.author_link}
            onChange={(e) => setForm({ ...form, author_link: e.target.value })}
            data-testid={`edit-author-link-${book.id}`}
          />
        </div>
      </div>

      {error && (
        <div className="text-sm text-[#B23B28] bg-[#FDEEEA] border border-[#F5BDAE] rounded-md p-3">
          {error}
        </div>
      )}

      <div className="bg-[#F8F6F0] border border-[#CFD6DC]/70 rounded-md p-3 text-xs text-[#2F2E2E]">
        <strong>Note:</strong> Saving will return your book to the admin queue for re-review before it's live again on the shelf.
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-md border border-[#CFD6DC] text-[#0E232E] font-semibold hover:bg-[#F0F3F5]"
          data-testid={`edit-cancel-${book.id}`}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="btn-teal px-5 py-2 rounded-md flex items-center gap-2"
          data-testid={`edit-save-${book.id}`}
        >
          {saving && <Loader2 className="animate-spin" size={14} />}
          Save changes
        </button>
      </div>
    </form>
  );
}

export default function MyBooks() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [tokenError, setTokenError] = useState(false);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/author/books?token=${encodeURIComponent(token)}`);
      setBooks(data);
    } catch (err) {
      setTokenError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // No token → request-link panel
  if (!token) {
    return (
      <main className="bg-page min-h-screen p-6 md:p-12">
        <div className="max-w-2xl mx-auto">
          <PageHeader />
          <RequestLinkPanel />
        </div>
      </main>
    );
  }

  if (tokenError) {
    return (
      <main className="bg-page min-h-screen p-6 md:p-12">
        <div className="max-w-2xl mx-auto">
          <PageHeader />
          <div className="bg-white border border-[#F5BDAE] rounded-2xl p-8 shadow-sm" data-testid="token-error">
            <h2 className="font-display text-2xl font-extrabold text-[#B23B28]">
              This link isn't valid
            </h2>
            <p className="mt-2 text-sm text-[#2F2E2E]">
              The link you used couldn't be verified. Request a fresh one below.
            </p>
            <div className="mt-6">
              <RequestLinkPanel />
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-page min-h-screen p-6 md:p-12" data-testid="my-books-page">
      <div className="max-w-3xl mx-auto">
        <PageHeader />

        <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[#0E232E] leading-tight">
          My books
        </h1>
        <p className="mt-2 text-sm text-[#2F2E2E]">
          Edit your details below. Any change returns the book to the admin queue for re-review.
        </p>

        {loading ? (
          <div className="text-[#0E232E]/60 italic mt-10 text-center">Loading…</div>
        ) : books.length === 0 ? (
          <div className="mt-10 bg-white border border-[#CFD6DC] rounded-xl p-10 text-center text-[#2F2E2E]">
            <p>No submissions found yet.</p>
            <Link
              to="/"
              className="inline-block mt-4 btn-teal px-5 py-2.5 rounded-full text-sm uppercase tracking-wider"
            >
              Submit a book
            </Link>
          </div>
        ) : (
          <ul className="mt-8 space-y-4" data-testid="my-books-list">
            {books.map((b) => (
              <li
                key={b.id}
                className="bg-white border border-[#CFD6DC] rounded-xl p-5 shadow-sm"
                data-testid={`my-book-${b.id}`}
              >
                <div className="flex flex-col sm:flex-row gap-4">
                  <div
                    className="w-20 h-28 flex-shrink-0 rounded-md overflow-hidden border border-[#CFD6DC] bg-[#F0F3F5]"
                    style={{
                      backgroundImage: b.cover_image_url
                        ? `url(${resolveCoverUrl(b.cover_image_url)})`
                        : undefined,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h3 className="font-display text-xl font-extrabold text-[#0E232E] leading-tight">
                          {b.title}
                        </h3>
                        <p className="text-xs text-[#2F2E2E] mt-1">
                          {b.age_group_label} · by {b.author_name}
                        </p>
                      </div>
                      {statusBadge(b.status)}
                    </div>
                    <p className="mt-2 text-sm text-[#2F2E2E] line-clamp-3">{b.description}</p>
                    {b.purchase_url && (
                      <a
                        href={b.purchase_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-xs text-[#2B328C] hover:underline font-semibold"
                      >
                        <ExternalLink size={11} /> {b.purchase_url}
                      </a>
                    )}
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => setEditingId(editingId === b.id ? null : b.id)}
                        className="btn-teal px-4 py-1.5 rounded-md text-xs uppercase tracking-wider flex items-center gap-1.5"
                        data-testid={`edit-${b.id}`}
                      >
                        <Edit3 size={12} /> {editingId === b.id ? "Close editor" : "Edit"}
                      </button>
                    </div>
                  </div>
                </div>
                {editingId === b.id && (
                  <BookEditor
                    book={b}
                    token={token}
                    onSaved={() => {
                      setEditingId(null);
                      load();
                    }}
                    onCancel={() => setEditingId(null)}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}
